<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import {
		AdvancedCardV2,
		CardGrid,
		Container,
		Modal,
		Spinner,
		Text,
	} from '@Shelly/shelly-design-system';
	import { format } from 'src/services/i18n';
	import HomeAvatar from 'src/components/Homes/HomeAvatar.svelte';
	import CrazyDivider from 'src/components/Profiles/CrazyDivider.svelte';
	import {
		getHomeDisplayName,
		getProfileDisplayName,
		getProfileEmailSubtitle,
		mapHomesResponse,
	} from 'src/helpers/houses/homes_helpers';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { profiles } from 'src/stores/features/profiles';
	import { homes, primeUnitsInfo } from 'src/stores/account/home';
	import { getUnitsInfo } from 'src/services/iotu/house-management';
	import type { Home } from 'src/types/Home';
	import type {
		AvailableSubUnit,
		AvailableSubUnits,
		AvailableUnits,
	} from 'src/types/responses/usersAuthResponse';

	export let showModal = false;
	export let units: AvailableUnits = {};
	export let subUnits: AvailableSubUnits = {};
	export let selectedUnitId: string | undefined;
	export let isLoading = false;
	export let showAddUnit = false;
	export let hideActiveUnit = false;
	export let accountTitle: string | undefined;
	export let accountEmail: string | undefined;
	export let authToken: string | undefined;
	export let userApiUrl: string | undefined;

	let localHomes: Home[] = [];
	let localHomesLoading = false;

	$: if (showModal && authToken && userApiUrl) {
		fetchLocalHomes(authToken, userApiUrl);
	}

	$: if (!showModal) {
		localHomes = [];
		localHomesLoading = false;
	}

	const fetchLocalHomes = async (token: string, apiUrl: string) => {
		localHomesLoading = true;
		try {
			const response = await getUnitsInfo({ token, userApiUrl: apiUrl });
			if (response?.isok) {
				localHomes = mapHomesResponse(response, '');
				// Reuse this discovery-token response for the imminent commit refresh
				// of the same account, avoiding a duplicate get_units_info.
				primeUnitsInfo(resolvedEmail, response);
			}
		} catch {
			// non-fatal: avatars fall back to the default house icon
		} finally {
			localHomesLoading = false;
		}
	};

	const dispatch = createEventDispatcher<{
		select: { unitId: string; subId?: number };
		addUnit: void;
	}>();

	let selectedId: string | undefined = selectedUnitId;

	$: activeFilter = hideActiveUnit ? selectedUnitId : undefined;

	$: selectedProfile = $profiles.find((profile) => profile.isSelected);
	$: resolvedEmail = accountEmail ?? selectedProfile?.email ?? $user_data.email ?? '';
	$: resolvedTitleSource = accountTitle ?? selectedProfile?.title ?? $user_settings.name;
	$: resolvedTitle = getProfileDisplayName(
		resolvedTitleSource,
		resolvedEmail,
		selectedProfile?.id ?? $user_data.user_id,
	);
	$: resolvedSubtitle = getProfileEmailSubtitle(resolvedTitleSource, resolvedEmail);

	// `get_units_info` is the source of truth for the rendered homes:
	//   1) the modal's own account-scoped fetch (`localHomes`), then
	//   2) the login `units`/`subUnits` props, enriched (name/avatar/sub) from
	//      `localHomes`/the homes store by id, then
	//   3) the global homes store as a last resort.
	$: homeList = buildHomeList(localHomes, $homes.list, units, subUnits, resolvedEmail);
	$: ownedHomesList = homeList.filter((home) => !home.isShared && home.id !== activeFilter);
	$: sharedHomesList = homeList.filter((home) => home.isShared && home.id !== activeFilter);
	$: hasUnits = ownedHomesList.length > 0;
	$: hasSubUnits = sharedHomesList.length > 0;
	$: showSectionLabels = hasUnits && hasSubUnits;
	$: showSpinner = localHomesLoading && homeList.length === 0;

	const getSubUnitName = (value: string | AvailableSubUnit): string => {
		if (typeof value === 'string') return value.trim() || 'Unknown';
		return value?.name?.trim() || value?.email?.trim() || 'Unknown';
	};

	const getSubId = (value: string | AvailableSubUnit): number | undefined => {
		if (typeof value === 'string') return undefined;
		return value?.sub;
	};

	// Owner email of a shared home: prefer the mapped `sharedByEmail` on the
	// resolved Home, fall back to the `AvailableSubUnit.email` (both come from
	// the `shared_by` API field).
	const getSharedByEmail = (value: string | AvailableSubUnit, home: Home | undefined): string => {
		if (home?.sharedByEmail) return home.sharedByEmail;
		if (typeof value !== 'string') return value?.email ?? '';
		return '';
	};

	// "Shared by {owner}" subtitle for the shared-home cards, matching the label
	// used in home management and the home pickers.
	const sharedBySubtitle = (ownerEmail: string) =>
		ownerEmail
			? {
					value: $format('settings_shared_by_label', {
						default: 'Shared by {email}',
						values: { email: ownerEmail },
					}),
					bold: false,
					textSmall: true,
				}
			: { value: '' };

	/** Build a `Home` from a login-props entry, enriching name/avatar/sub from get_units_info by id. */
	const enrichHome = (
		id: string,
		fallbackName: string,
		isShared: boolean,
		subId: number | undefined,
		richHomes: Home[],
	): Home => {
		const match = richHomes.find((home) => home.id === id);
		return {
			id,
			sub: subId ?? match?.sub,
			name: match?.name?.trim() || fallbackName,
			avatar: match?.avatar,
			plan: match?.plan ?? 'standard',
			isActive: match?.isActive ?? false,
			isShared,
			sharedByEmail: match?.sharedByEmail,
		};
	};

	const buildHomeList = (
		ownFetched: Home[],
		storeHomes: Home[],
		ownedProps: AvailableUnits,
		sharedProps: AvailableSubUnits,
		email: string,
	): Home[] => {
		if (ownFetched.length > 0) return ownFetched;

		const owned = Object.entries(ownedProps).map(([id, name]) =>
			enrichHome(id, getHomeDisplayName(name, email), false, undefined, storeHomes),
		);
		const shared = Object.entries(sharedProps).map(([id, value]) => {
			const home = enrichHome(id, getSubUnitName(value), true, getSubId(value), storeHomes);
			// Preserve the owner email from the login props when get_units_info has
			// not resolved it, so the "Shared by {owner}" subtitle still renders.
			return { ...home, sharedByEmail: getSharedByEmail(value, home) || undefined };
		});
		const fromProps = [...owned, ...shared];
		if (fromProps.length > 0) return fromProps;

		return storeHomes;
	};

	const onCloseModal = () => {
		if (isLoading) return;
		showModal = false;
	};

	const onSelectUnit = (unitId: string, subId?: number) => {
		if (isLoading) return;
		selectedId = unitId;
		dispatch('select', { unitId, subId });
	};

	const onAddUnit = () => {
		if (isLoading) return;
		dispatch('addUnit');
	};
</script>

<Modal
	{showModal}
	title={$format('select_unit_from_account', {
		default: 'Select home from',
	})}
	on:close={onCloseModal}
	removeScrollPadding
>
	<Container slot="content" padding="scrollPaddings">
		<div class="position-relative">
			<div class="unit-modal-account mb-m">
				<Text noWrap styleType="body">{resolvedTitle}</Text>
				{#if resolvedSubtitle}
					<Text noWrap styleType="tiny" class="text-color-3">{resolvedSubtitle}</Text>
				{/if}
			</div>
			<CrazyDivider />
			<div class="unit-modal-homes mt-m">
				{#if hasUnits}
					{#if showSectionLabels}
						<Text class="mb-s" styleType="label-large">
							{$format('unit_section_your_homes', { default: 'Your Homes' })}
						</Text>
					{/if}
					<CardGrid gridGap="s" sm={200} md={200} xl={200} noPaddingBottom>
						{#each ownedHomesList as home (home.id)}
							<AdvancedCardV2
								title={home.name}
								isCondensed
								isFocusable
								selected={selectedId === home.id}
								class="width-full"
								aria-label={$format('label_select_home', {
									default: 'Select home {name}',
									values: { name: home.name },
								})}
								on:click={() => onSelectUnit(home.id)}
							>
								<HomeAvatar slot="left" {home} size="m" />
							</AdvancedCardV2>
						{/each}
					</CardGrid>
				{/if}
				{#if hasSubUnits}
					<Text class={hasUnits ? 'mt-m mb-s' : 'mb-s'} styleType="label-large">
						{$format('unit_section_shared_homes', { default: 'Shared Homes' })}
					</Text>
					<CardGrid gridGap="s" sm={200} md={200} xl={200} noPaddingBottom>
						{#each sharedHomesList as home (home.id)}
							<AdvancedCardV2
								title={home.name}
								subtitle={sharedBySubtitle(home.sharedByEmail ?? '')}
								isCondensed
								isFocusable
								selected={selectedId === home.id}
								class="width-full"
								aria-label={$format('label_select_home', {
									default: 'Select home {name}',
									values: { name: home.name },
								})}
								on:click={() => onSelectUnit(home.id, home.sub)}
							>
								<HomeAvatar slot="left" {home} size="m" />
							</AdvancedCardV2>
						{/each}
					</CardGrid>
				{/if}
			</div>
			{#if isLoading || showSpinner}
				<div class="unit-modal-loading-overlay">
					<Spinner size="m" />
				</div>
			{/if}
		</div>
		<!-- {#if showAddUnit}
			<Button
				class="mt-m width-full"
				styleType="secondary"
				disabled={isLoading}
				on:click={onAddUnit}
			>
				{$format('button_add_home', { default: 'Add new home' })}
			</Button>
		{/if} -->
	</Container>
</Modal>

<style>
	.unit-modal-account {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.unit-modal-loading-overlay {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background: var(--color-background-primary-transparent, rgba(255, 255, 255, 0.7));
		z-index: 1;
		border-radius: var(--border-radius-m, 8px);
	}
</style>
